/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import {
    Carousel,
    CarouselGetRequest,
    Carousels,
    CarouselsApi,
    Configuration,
    ConfigurationParameters,
    OfferType,
    ScreenGetRequest,
    ScreensApi,
    WatchHistoryPostApi,
    WatchHistoryPostRequest,
} from 'client-code/wrs-feed-service';

import { CommonHeaders } from '../CommonHeaders';
import { Constants } from '../constants';
import { IWrsFeedServiceRepository } from '../interfaces/IWrsFeedServiceRepository';
import { Log } from '../logging';
import { TServicesParams } from '../types';
import { UniqueIdentifier } from '../utils';

/**
 * @inheritDoc
 */
export class WrsFeedServiceRepositoryImpl implements IWrsFeedServiceRepository {
    private static readonly TAG = 'WrsFeedServiceRepositoryImpl';

    private mParams: TServicesParams;

    public constructor(params: TServicesParams) {
        this.mParams = params;
    }

    // eslint-disable-next-line class-methods-use-this
    private buildCommonHeaders(featureArea: string): CommonHeaders {
        return new CommonHeaders(featureArea);
    }

    private getConfigurationParameters(
        commonHeaders: CommonHeaders['headers'],
    ): ConfigurationParameters {
        return {
            apiKey: this.mParams.accessToken,
            basePath: this.mParams.basePath,
            baseOptions: {
                timeout: Constants.SERVICE_CONNECTION_TIMEOUT,
                headers: {
                    ...commonHeaders,
                    'Accept-Encoding': 'gzip',
                },
            },
        };
    }

    private getCommonRequestParams(commonHeaders: CommonHeaders['headers']) {
        return {
            applicationFeatureArea: commonHeaders.ApplicationFeatureArea,
            applicationVersion: commonHeaders.ApplicationVersion,
            bodyId: this.mParams.bodyId,
            applicationName: commonHeaders.ApplicationName,
            deviceType: commonHeaders.DeviceType,
            productName: commonHeaders.ProductName,
            acceptLanguage: commonHeaders['Accept-Language'],
            timeZoneName: this.mParams.timeZoneName,
            xAmznRequestId: UniqueIdentifier.generateXAmznRequestId(),
            whiny: this.mParams.whiny || 'false',
        };
    }

    public async getScreen(
        screenName: string,
        adUnitsSupported?: Array<string>,
        count?: number,
        initialFeedItemCount?: number,
        includeAdult?: boolean,
        includeIpVod?: boolean,
        includeSportsPass?: boolean,
        pageCursor?: string,
    ): Promise<Carousels> {
        try {
            const commonHeaders = this.buildCommonHeaders('WrsFeedService').headers;
            const config = this.getConfigurationParameters(commonHeaders);
            const configuration = new Configuration(config);
            const screensApi = new ScreensApi(configuration);

            const requestParameters: ScreenGetRequest = {
                screenName,
                adUnitsSupported,
                count,
                initialFeedItemCount,
                includeAdult,
                includeIpVod,
                includeSportsPass,
                pageCursor,
                ...this.getCommonRequestParams(commonHeaders),
            };

            const response = await screensApi.screenGet(requestParameters);
            return response.data;
        } catch (error: any) {
            Log.e(WrsFeedServiceRepositoryImpl.TAG, 'getScreen', error.message, {
                screenName,
                count,
                initialFeedItemCount,
                includeAdult,
                pageCursor,
            });
            throw error;
        }
    }

    public async getCarousel(
        carouselName: string,
        offset?: number,
        count?: number,
        includeAdult?: boolean,
        includeIpVod?: boolean,
        includeSportsPass?: boolean,
    ): Promise<Carousel> {
        try {
            const commonHeaders = this.buildCommonHeaders('WrsFeedService').headers;
            const config = this.getConfigurationParameters(commonHeaders);
            const configuration = new Configuration(config);
            const carouselsApi = new CarouselsApi(configuration);

            const requestParameters: CarouselGetRequest = {
                carouselName,
                offset,
                count,
                includeAdult,
                includeIpVod,
                includeSportsPass,
                ...this.getCommonRequestParams(commonHeaders),
            };

            const response = await carouselsApi.carouselGet(requestParameters);
            return response.data;
        } catch (error: any) {
            Log.e(WrsFeedServiceRepositoryImpl.TAG, 'getCarousel', error.message, {
                carouselName,
                offset,
                count,
                includeAdult,
            });
            throw error;
        }
    }

    public async postWatchHistory(
        objectType: OfferType,
        objectId: string,
        collectionId?: string,
    ): Promise<void> {
        try {
            const commonHeaders = this.buildCommonHeaders('WrsFeedService').headers;
            const config = this.getConfigurationParameters(commonHeaders);
            const configuration = new Configuration(config);
            const watchHistoryPostApi = new WatchHistoryPostApi(configuration);

            const requestParameters: WatchHistoryPostRequest = {
                objectType,
                objectId,
                collectionId,
                ...this.getCommonRequestParams(commonHeaders),
            };

            await watchHistoryPostApi.watchHistoryPost(requestParameters);
        } catch (error: any) {
            Log.e(WrsFeedServiceRepositoryImpl.TAG, 'postWatchHistory', error.message, {
                objectType,
                objectId,
            });
            throw error;
        }
    }
}
